import "./AppPayPaySchedules.css";
import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUIStates, selectUser, setUIStates } from "../../../redux/features/appData";
import { CheckPaySchedule, Workspace, CheckPayFrequency, Company, CheckCompany, ApiResponse, CheckResponseError, isCheckError } from "winslow-core";
import { 
    apiCheckListCompanyPaySchedules, 
    apiCheckUpdatePaySchedule,
    apiCheckCreatePaySchedule,
} from "../../../services/PayrollService";
import { Button, Icon } from "../../../ds2/components";
import { Plus, Edit } from "lucide-react";
import { ActionLink } from "../../../ds2/components/ActionLink";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../../config";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { GenericSelect, GenericSelectOption } from "../../shared/GenericSelect";

type AppPayPaySchedulesProps = {
  company: Company | null;
  checkCompany: CheckCompany | null;
};

export const AppPayPaySchedules = ({ company, checkCompany }: AppPayPaySchedulesProps) => {
    const dispatch = useDispatch();

    const uiStates = useSelector(selectUIStates);
    const storeUser = useSelector(selectUser);

    const workspace = storeUser?.workspaces.find((w: Workspace) => w.id === storeUser.currentWorkspaceId);
    const checkCompanyId = company?.checkCompanyId || checkCompany?.id;

    const [checkPaySchedules, setCheckPaySchedules] = useState<CheckPaySchedule[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [showAddForm, setShowAddForm] = useState<boolean>(false);
    const [editingPaySchedule, setEditingPaySchedule] = useState<CheckPaySchedule | null>(null);

    const [formData, setFormData] = useState<Omit<CheckPaySchedule, "id">>({
        name: "",
        company: checkCompanyId || "",
        pay_frequency: "biweekly",
        first_payday: "",
        second_payday: null,
        first_period_end: "",
        metadata: {},
    });

    const [checkResponseError, setCheckResponseError] = useState<CheckResponseError | undefined>(undefined);

    // Pay frequency options
    const payFrequencyOptions: GenericSelectOption[] = [
        { displayName: "Bi-weekly", value: "biweekly" },
        { displayName: "Semi-monthly", value: "semimonthly" },
    ];

    // Sort pay schedules alphabetically by name
    const sortedPaySchedules = useMemo(() => {
        return [...checkPaySchedules].sort((a, b) => 
            (a.name || "").localeCompare(b.name || "")
        );
    }, [checkPaySchedules]);

    useEffect(() => {
        dispatch(setUIStates({...uiStates, hrisLocation: "Payroll Configuration"}));
        loadCheckPaySchedules();
    }, [storeUser, checkCompanyId]);

    const loadCheckPaySchedules = async () => {
        if (!checkCompanyId) return;
        
        setLoading(true);
        try {
            const apiResponse = await apiCheckListCompanyPaySchedules(checkCompanyId);
            const paySchedules = apiResponse?.data;
            setCheckResponseError(apiResponse?.checkResponseError);

            if (!apiResponse || isCheckError(apiResponse) || !paySchedules) {
                toast("Error loading pay schedules", toastFail);
                return;
            }

            setCheckPaySchedules(paySchedules || []);
        } catch (error) {
            console.error("[AppPayPaySchedules] Error loading pay schedules:", error);
            toast("Error loading pay schedules", toastFail);
        } finally {
            setLoading(false);
        }
    };

    const formatDateToYYYYMMDD = (date: Date | null): string => {
        if (!date) return "";
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const parseYYYYMMDDToDate = (dateString: string | null | undefined): Date | null => {
        if (!dateString) return null;
        const [year, month, day] = dateString.split("-").map(Number);
        return new Date(year, month - 1, day);
    };

    const resetForm = () => {
        setFormData({
            name: "",
            company: checkCompanyId || "",
            pay_frequency: "biweekly",
            first_payday: "",
            second_payday: null,
            first_period_end: "",
            metadata: {},
        });
        setEditingPaySchedule(null);
        setShowAddForm(false);
    };

    const handleAddClick = () => {
        resetForm();
        setShowAddForm(true);
    };

    const handleEditClick = (paySchedule: CheckPaySchedule) => {
        // Ensure pay_frequency is one of the allowed options, default to biweekly if not
        const allowedFrequencies = ["biweekly", "semimonthly"];
        const payFrequency = paySchedule.pay_frequency && allowedFrequencies.includes(paySchedule.pay_frequency)
            ? paySchedule.pay_frequency
            : "biweekly";
        
        setFormData({
            name: paySchedule.name || "",
            company: paySchedule.company || checkCompanyId || "",
            pay_frequency: payFrequency as CheckPayFrequency,
            first_payday: paySchedule.first_payday || "",
            second_payday: paySchedule.second_payday || null,
            first_period_end: paySchedule.first_period_end || "",
            metadata: paySchedule.metadata || {},
        });
        setEditingPaySchedule(paySchedule);
        setShowAddForm(true);
    };

    const handleSave = async () => {
        if (!checkCompanyId) {
            toast("Check company ID not found", toastFail);
            return;
        }

        if (!formData.name.trim()) {
            toast("Name is required", toastFail);
            return;
        }

        if (!formData.first_payday.trim()) {
            toast("First payday is required", toastFail);
            return;
        }

        if (!formData.first_period_end.trim()) {
            toast("First period end is required", toastFail);
            return;
        }

        const payload: CheckPaySchedule = {
            id: editingPaySchedule?.id || "", 
            name: formData.name.trim(),
            company: checkCompanyId || "",
            pay_frequency: formData.pay_frequency,
            first_payday: formData.first_payday,
            second_payday: null,
            first_period_end: formData.first_period_end,
            metadata: editingPaySchedule?.metadata || formData.metadata || {},
        };

        try {
            if (editingPaySchedule) {
                const apiResponse = await apiCheckUpdatePaySchedule(editingPaySchedule.id, payload);
                const updatedCheckPaySchedule = apiResponse?.data;
                setCheckResponseError(apiResponse?.checkResponseError);

                if (!apiResponse || isCheckError(apiResponse) || !updatedCheckPaySchedule) {
                    toast("Error updating pay schedule", toastFail);
                    return;
                }

                if (updatedCheckPaySchedule) {
                    toast("Pay schedule updated successfully", toastSuccess);
                    await loadCheckPaySchedules();
                    resetForm();
                } else {
                    toast("Error updating pay schedule", toastFail);
                }
            } else {
                
                const apiResponse = await apiCheckCreatePaySchedule(checkCompanyId, payload);
                const createdCheckPaySchedule = apiResponse?.data;
                setCheckResponseError(apiResponse?.checkResponseError);

                if (!apiResponse || isCheckError(apiResponse) || !createdCheckPaySchedule) {
                    toast("Error creating pay schedule", toastFail);
                    return;
                }

                if (createdCheckPaySchedule) {
                    toast("Pay schedule created successfully", toastSuccess);
                    await loadCheckPaySchedules();
                    resetForm();
                } else {
                    toast("Error creating pay schedule", toastFail);
                }
            }
        } catch (error) {
            console.error("[AppPayPaySchedules] Error saving pay schedule:", error);
            toast("Error saving pay schedule", toastFail);
        }
    };

    const formatDate = (dateString: string | null | undefined): string => {
        if (!dateString) return "-";
        const date = parseYYYYMMDDToDate(dateString);
        if (!date) return "-";
        return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    };

    const formatPayFrequency = (frequency: CheckPayFrequency): string => {
        const option = payFrequencyOptions.find(opt => opt.value === frequency);
        return option?.displayName || frequency;
    };

    return (
        <div className="app-pay-schedules">
            <div className="app-pay-schedules-header">
                {!showAddForm && (
                    <ActionLink
                        label="Add Pay Schedule"
                        leftIcon={<Plus size={16} />}
                        onClick={handleAddClick}
                    />
                )}
            </div>

            {showAddForm && (
                <div className="app-pay-schedules-form">
                    <div className="app-pay-schedules-form-header">
                        <h3>{editingPaySchedule ? "Edit Pay Schedule" : "Add Pay Schedule"}</h3>
                    </div>
                    <div className="app-pay-schedules-form-content">
                        <div className="app-custom-property-label">
                            Name *
                        </div>
                        <div className="app-custom-property-value">
                            <input
                                type="text"
                                placeholder="Enter pay schedule name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>

                        <div className="app-custom-property-label">
                            Pay Frequency *
                        </div>
                        <div className="app-custom-property-value">
                            <GenericSelect
                                options={payFrequencyOptions}
                                selected={payFrequencyOptions.find(opt => opt.value === formData.pay_frequency)}
                                onChange={(value: any) => {
                                    setFormData({ ...formData, pay_frequency: value as CheckPayFrequency });
                                }}
                                maxHeight="200px"
                            />
                        </div>

                        <div className="app-custom-property-label">
                            First Payday *
                        </div>
                        <div className="app-custom-property-value">
                            <DatePicker
                                selected={parseYYYYMMDDToDate(formData.first_payday)}
                                dateFormat={"MM/dd/yyyy"}
                                onChange={(date) => {
                                    setFormData({ ...formData, first_payday: formatDateToYYYYMMDD(date as Date | null) });
                                }}
                                className="date-picker-input"
                                wrapperClassName="app-date-picker-wrapper"
                            />
                        </div>

                        <div className="app-custom-property-label">
                            First Period End *
                        </div>
                        <div className="app-custom-property-value">
                            <DatePicker
                                selected={parseYYYYMMDDToDate(formData.first_period_end)}
                                dateFormat={"MM/dd/yyyy"}
                                onChange={(date) => {
                                    setFormData({ ...formData, first_period_end: formatDateToYYYYMMDD(date as Date | null) });
                                }}
                                className="date-picker-input"
                                wrapperClassName="app-date-picker-wrapper"
                            />
                        </div>
                    </div>
                    <div className="app-pay-schedules-form-actions">
                        <Button
                            label="Cancel"
                            variant="secondary"
                            onClick={resetForm}
                        />
                        <Button
                            label={editingPaySchedule ? "Update" : "Create"}
                            variant="primary"
                            onClick={handleSave}
                            disabled={!formData.name.trim() || !formData.first_payday.trim() || !formData.first_period_end.trim()}
                        />
                    </div>
                </div>
            )}

            {!showAddForm && (
                <>
                    {loading ? (
                        <div className="app-pay-schedules-loading">
                            Loading pay schedules...
                        </div>
                    ) : sortedPaySchedules.length === 0 ? (
                        <div className="app-pay-schedules-empty">
                            <p>No pay schedules found. Click "Add Pay Schedule" to create one.</p>
                        </div>
                    ) : (
                        <div className="app-pay-schedules-table-container">
                            <table className="app-pay-schedules-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Pay Frequency</th>
                                        <th>First Payday</th>
                                        <th>First Period End</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedPaySchedules.map((paySchedule) => (
                                        <tr key={paySchedule.id} className="app-pay-schedules-row">
                                            <td className="app-pay-schedules-name-cell">
                                                <strong>{paySchedule.name || "-"}</strong>
                                            </td>
                                            <td className="app-pay-schedules-frequency-cell">
                                                {formatPayFrequency(paySchedule.pay_frequency)}
                                            </td>
                                            <td className="app-pay-schedules-payday-cell">
                                                {formatDate(paySchedule.first_payday)}
                                            </td>
                                            <td className="app-pay-schedules-period-end-cell">
                                                {formatDate(paySchedule.first_period_end)}
                                            </td>
                                            <td className="app-pay-schedules-actions-cell">
                                                <div className="app-pay-schedules-actions-group">
                                                    <Icon
                                                        variant="tertiary"
                                                        size="md"
                                                        icon={<Edit size={16} />}
                                                        onClick={() => handleEditClick(paySchedule)}
                                                        title="Edit"
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};


